import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import { convertAmountToWords } from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";

export const CustomerPOInvoice = ({
  headDetails,
  companyData,
  itemDetails,
  rowData,
  invoiceTableData,
  selectedReportName,
}) => {
  const toWords = new ToWords();

  const organizationData = JSON.parse(localStorage.getItem("organisation"));

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const customerOrderNo = headDetails?.tran_no || "--";
  const actualCustomerPoNo =
    headDetails?.customer_po_no ||
    headDetails?.po_no ||
    headDetails?.customer_po ||
    headDetails?.po_number ||
    headDetails?.dc_no ||
    "--";

  const noteText =
    headDetails?.narration || headDetails?.remark || headDetails?.remarks || "";
  const notes = noteText ? [noteText] : [];

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const formatCompanyAddress = (companyData) => {
    if (!companyData) return "N/A";

    const parts = [
      companyData.address?.trim(),
      companyData.city?.trim(),
    ].filter(Boolean);

    const pin = companyData.pincode ? `- ${companyData.pincode}` : "";

    return parts.length > 0 || pin
      ? `${parts.join(", ")} ${pin}`.trim()
      : "N/A";
  };

  const excludedColumnTitles = [
    "sgst per",
    "sgst",
    "cgst per",
    "cgst",
    "igst per",
    "igst",
  ];
  const columnOrder = [
    "product name",
    "product description",
    "quantity",
    "uom",
    "rate",
    "amount",
    "gross amount",
  ];
  const visibleFields = [...rowData]
    .filter((field) => !field.is_internal_field)
    .filter((field) => {
      const title = (field.title || field.field_name || "").toLowerCase().trim();
      return !excludedColumnTitles.includes(title);
    })
    .sort((a, b) => {
      const at = (a.title || a.field_name || "").toLowerCase().trim();
      const bt = (b.title || b.field_name || "").toLowerCase().trim();
      const ai = columnOrder.indexOf(at);
      const bi = columnOrder.indexOf(bt);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

  const calculateTaxFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGst: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        igstPercent: 0,
      };
    }

    const taxTotals = itemDetails.reduce(
      (totals, item) => {
        const cgstAmt = parseFloat(item.cgst_amt || 0);
        const sgstAmt = parseFloat(item.sgst_amt || 0);
        const igstAmt = parseFloat(item.igst_amt || 0);
        const cgstPer = parseFloat(item.cgst_per || 0);
        const sgstPer = parseFloat(item.sgst_per || 0);
        const igstPer = parseFloat(item.igst_per || 0);

        return {
          cgst: totals.cgst + cgstAmt,
          sgst: totals.sgst + sgstAmt,
          igst: totals.igst + igstAmt,
          cgstPer: totals.cgstPer + cgstPer,
          sgstPer: totals.sgstPer + sgstPer,
          igstPer: totals.igstPer + igstPer,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, cgstPer: 0, sgstPer: 0, igstPer: 0 },
    );

    const totalGst = taxTotals.cgst + taxTotals.sgst + taxTotals.igst;

    const itemCount = itemDetails.length;
    const cgstPercent =
      itemCount > 0 ? (taxTotals.cgstPer / itemCount).toFixed(2) : 0;
    const sgstPercent =
      itemCount > 0 ? (taxTotals.sgstPer / itemCount).toFixed(2) : 0;
    const igstPercent =
      itemCount > 0 ? (taxTotals.igstPer / itemCount).toFixed(2) : 0;

    return {
      cgst: taxTotals.cgst.toFixed(2),
      sgst: taxTotals.sgst.toFixed(2),
      igst: taxTotals.igst.toFixed(2),
      totalGst: totalGst.toFixed(2),
      cgstPercent,
      sgstPercent,
      igstPercent,
    };
  };

  const calculateTotalFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return 0;
    }

    const totalAmount = itemDetails.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      return sum + amount;
    }, 0);

    return totalAmount;
  };

  const { cgst, sgst, igst, totalGst, cgstPercent, sgstPercent, igstPercent } =
    calculateTaxFromItems();
  const totalAmountFromItems = calculateTotalFromItems();
  const finalTotal = totalAmountFromItems + parseFloat(totalGst);
  const amountInWords = convertAmountToWords(finalTotal, toWords);

  return (
    <div className="invoice-container sales-customer-order-layout customer-order-page-container">
      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData) || "N/A"}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
            </p>
          </div>
        </div>

        <div className="invoice-center">
          <h2 className="invoice-title" style={{ textAlign: "center", margin: 0 }}>
            Sales Order
          </h2>
        </div>

        <div className="invoice-right">
          {[
            { label: "Sales Order No", value: customerOrderNo },
            {
              label: "Sales Order Date",
              value: formatDate(
                headDetails?.sales_order_date ||
                  headDetails?.tran_date ||
                  headDetails?.date ||
                  headDetails?.created_at,
              ),
            },
            { label: "Customer PO No", value: actualCustomerPoNo },
            {
              label: "Customer PO Date",
              value: formatDate(
                headDetails?.customer_po_date ||
                  headDetails?.po_date ||
                  headDetails?.date ||
                  headDetails?.created_at,
              ),
            },
            {
              label: "Quotation No",
              value: headDetails?.quotation_no || "",
            },
            {
              label: "Quotation Date",
              value: formatDate(headDetails?.quotation_date),
            },
            {
              label: "Delivery Date",
              value: formatDate(headDetails?.delivery_date),
            },
            {
              label: "Assigned Engineer",
              value: headDetails?.assigned_engineer || "--",
            },
          ].map((item, index) => (
            <div key={index} className="purchase-detail">
              <p className="detail-label">
                <strong>{item.label}:</strong>
              </p>
              <div className="detail-value">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="invoice-supplier-details-container invoice-supplier-details-container-with-terms">
        <div className="invoice-supplier-section">
          <h4 className="invoice-section-title">Name of Customer:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {companyData.company_name || "N/A"}
            </span>
            <br />
            Address: {companyData.company_address || "N/A"}
            <br />
            State: Karnataka
          </p>
        </div>

        <div className="invoice-delivery-section">
          <h4 className="invoice-section-title">Delivery at:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {companyData.company_name || "N/A"}
            </span>
            <br />
            Address: {companyData.company_address || "Address not specified"}
            <br />
            GSTIN: {companyData.company_gst_no || "N/A"}
            <br />
            State: Karnataka
          </p>
        </div>

        <div className="invoice-terms-section">
          <h4 className="invoice-section-title">Terms and Conditions:</h4>
          <p className="invoice-section-text">
            {headDetails?.payment_terms || organizationData?.payment_terms || ""}
          </p>
        </div>
      </div>

      <div className="invoice-overflow-x-scroll">
        <table className="invoice-table">
          <thead className="invoice-thead">
            <tr>
              <th className="invoice-th" style={{ borderLeftWidth: "0px" }}>
                No.
              </th>
              {visibleFields.map((field, index) => (
                <th
                  key={index}
                  className="invoice-th"
                  style={
                    index === visibleFields.length - 1
                      ? { borderRightWidth: "0px" }
                      : {}
                  }
                >
                  {field.title || field.field_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itemDetails.map((item, index) => (
              <tr key={index}>
                <td className="invoice-td" style={{ borderLeftWidth: "0px" }}>
                  {index + 1}
                </td>
                {visibleFields.map((field, idx, arr) => (
                    <td
                      key={idx}
                      className="invoice-td"
                      style={
                        idx === arr.length - 1
                          ? { borderRightWidth: "0px" }
                          : {}
                      }
                    >
                      {item[field.field_name] || "-"}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="invoice-tax-summary">
        <div className="invoice-tax-summary-row">
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">CGST @ {cgstPercent}%</p>
            <p className="invoice-tax-summary-value">{formatCurrency(cgst)}</p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">SGST @ {sgstPercent}%</p>
            <p className="invoice-tax-summary-value">{formatCurrency(sgst)}</p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">IGST @ {igstPercent}%</p>
            <p className="invoice-tax-summary-value">{formatCurrency(igst)}</p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">Net Amount (Before GST)</p>
            <p className="invoice-tax-summary-value">
              {formatCurrency(totalAmountFromItems || 0)}
            </p>
          </div>
        </div>
        <div className="invoice-tax-summary-row">
          <div className="invoice-tax-summary-note-region">
            <p
              className="invoice-note-heading"
              style={{
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "12px",
              }}
            >
              <strong>Note: </strong>
            </p>
            {notes.length > 0 && (
              <p
                className="invoice-note-text"
                style={{ paddingLeft: "16px", paddingRight: "16px" }}
              >
                {notes.join(" ")}
              </p>
            )}
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">Total GST Amount</p>
            <p className="invoice-tax-summary-value">
              {formatCurrency(totalGst)}
            </p>
          </div>
        </div>
        <div className="invoice-tax-summary-row">
          <div className="invoice-tax-summary-note-region">
            <p className="invoice-total-amount">
              <strong>Total Amount: </strong>
              {headDetails?.net_amt ? `${amountInWords} Only` : "N/A"}
            </p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">Total</p>
            <p className="invoice-tax-summary-value">
              {formatCurrency(finalTotal)}
            </p>
          </div>
        </div>
      </div>

      <div className="invoice-footer-meta-block">
        <div
          className="workorder-header-row workorder-meta-row"
          style={{
            borderLeft: "1px solid #9ca3af",
            borderRight: "1px solid #9ca3af",
            borderTop: "none",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          }}
        >
          <div className="workorder-meta-cell">
            <span className="workorder-meta-label">Prepared By:</span>
            <span className="workorder-meta-value">
              {headDetails?.prepared_by || ""}
            </span>
          </div>
          <div className="workorder-meta-cell">
            <span className="workorder-meta-label">Revised By:</span>
            <span className="workorder-meta-value">
              {headDetails?.revised_by || ""}
            </span>
          </div>
          <div className="workorder-meta-cell">
            <span className="workorder-meta-label">Approved By:</span>
            <span className="workorder-meta-value">
              {headDetails?.approved_by || ""}
            </span>
          </div>
        </div>
      </div>

      <div className="invoice-footer-meta-block">
        <div
          className="workorder-header-row workorder-meta-row"
          style={{
            borderLeft: "1px solid #9ca3af",
            borderRight: "1px solid #9ca3af",
            borderTop: "none",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          }}
        >
          <div className="workorder-meta-cell">
            <span className="workorder-meta-label">Ref No:</span>
            <span className="workorder-meta-value">FP/PO/01</span>
          </div>
          <div className="workorder-meta-cell">
            <span className="workorder-meta-label">Revision No:</span>
            <span className="workorder-meta-value">01</span>
          </div>
          <div className="workorder-meta-cell">
            <span className="workorder-meta-label">Revision Date:</span>
            <span className="workorder-meta-value">06/06/2026</span>
          </div>
        </div>
      </div>

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons" style={{ paddingTop: "40px" }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName="invoice"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".customer-order-page-container"
        />
      </div>
    </div>
  );
};
